import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, AlertCircle, CheckCircle, XCircle } from 'lucide-react';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link
            to="/"
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-3xl font-bold text-neutral-900">Terms of Service</h1>
          <p className="text-neutral-600 mt-2">Last updated: January 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8 space-y-8">
          {/* Introduction */}
          <section>
            <div className="flex items-center mb-4">
              <FileText className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-neutral-900">Agreement to Terms</h2>
            </div>
            <p className="text-neutral-700 leading-relaxed">
              These Terms of Service ("Terms") govern your access to and use of Finance BPA
              ("Service," "Platform," "we," "our," or "us"), an enterprise financial planning and
              analysis platform. By accessing or using our Service, you agree to be bound by these
              Terms.
            </p>
            <p className="text-neutral-700 leading-relaxed mt-4">
              If you do not agree to these Terms, you may not access or use the Service. These
              Terms apply to all users, including visitors, registered users, and organizations.
            </p>
          </section>

          {/* Eligibility */}
          <section>
            <div className="flex items-center mb-4">
              <CheckCircle className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-neutral-900">Eligibility and Account</h2>
            </div>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">Age Requirement</h3>
                <p className="text-neutral-700">
                  You must be at least 18 years old and have the legal capacity to enter into
                  binding agreements to use this Service.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">Account Registration</h3>
                <ul className="list-disc list-inside text-neutral-700 space-y-1 ml-4">
                  <li>You must provide accurate, current, and complete information during registration</li>
                  <li>You are responsible for maintaining the confidentiality of your account credentials</li>
                  <li>You must notify us immediately of any unauthorized access to your account</li>
                  <li>You are responsible for all activities that occur under your account</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">Organization Accounts</h3>
                <p className="text-neutral-700">
                  If you create an account on behalf of an organization, you represent that you have
                  the authority to bind that organization to these Terms.
                </p>
              </div>
            </div>
          </section>

          {/* Use of Service */}
          <section>
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Acceptable Use</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">Permitted Uses</h3>
                <p className="text-neutral-700 mb-2">You may use the Service to:</p>
                <ul className="list-disc list-inside text-neutral-700 space-y-1 ml-4">
                  <li>Create and manage financial models, budgets, and forecasts</li>
                  <li>Generate financial statements and reports</li>
                  <li>Integrate with accounting systems (QuickBooks, Xero, NetSuite, etc.)</li>
                  <li>Collaborate with team members on financial planning</li>
                  <li>Access AI-powered financial insights and recommendations</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">Prohibited Uses</h3>
                <p className="text-neutral-700 mb-2">You agree not to:</p>
                <ul className="list-disc list-inside text-neutral-700 space-y-1 ml-4">
                  <li>Violate any applicable laws, regulations, or third-party rights</li>
                  <li>Upload malicious code, viruses, or harmful software</li>
                  <li>Attempt to gain unauthorized access to the Service or other accounts</li>
                  <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
                  <li>Use the Service for illegal financial activities or fraud</li>
                  <li>Interfere with or disrupt the Service or servers</li>
                  <li>Impersonate any person or entity or misrepresent your affiliation</li>
                  <li>Scrape, crawl, or use automated tools to access the Service without permission</li>
                  <li>Share your account credentials with unauthorized parties</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Financial Data Accuracy */}
          <section>
            <div className="flex items-center mb-4">
              <AlertCircle className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-neutral-900">Financial Data and Accuracy</h2>
            </div>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">Your Responsibility</h3>
                <p className="text-neutral-700">
                  You are solely responsible for the accuracy, completeness, and legality of all
                  financial data you upload or enter into the Service. You represent and warrant
                  that:
                </p>
                <ul className="list-disc list-inside text-neutral-700 space-y-1 ml-4 mt-2">
                  <li>All financial data is accurate and reflects actual business transactions</li>
                  <li>You have the right to upload and process such data</li>
                  <li>You comply with all applicable accounting standards and regulations</li>
                  <li>You maintain proper backups of your financial data</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">Service Limitations</h3>
                <p className="text-neutral-700">
                  While we provide tools for financial analysis and forecasting, we do not:
                </p>
                <ul className="list-disc list-inside text-neutral-700 space-y-1 ml-4 mt-2">
                  <li>Guarantee the accuracy of forecasts or predictions</li>
                  <li>Provide financial, legal, or tax advice</li>
                  <li>Verify the accuracy of data imported from third-party systems</li>
                  <li>Assume responsibility for business decisions made based on our Service</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Intellectual Property</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">Our Property</h3>
                <p className="text-neutral-700">
                  The Service, including its software, design, features, and content, is owned by
                  Finance BPA and protected by copyright, trademark, and other intellectual property
                  laws. You may not copy, modify, distribute, or create derivative works without
                  our written permission.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">Your Content</h3>
                <p className="text-neutral-700">
                  You retain ownership of all financial data and content you upload to the Service.
                  By using the Service, you grant us a limited license to:
                </p>
                <ul className="list-disc list-inside text-neutral-700 space-y-1 ml-4 mt-2">
                  <li>Store, process, and display your data to provide the Service</li>
                  <li>Generate reports, forecasts, and insights based on your data</li>
                  <li>Use anonymized, aggregated data for service improvement and analytics</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Payment Terms */}
          <section>
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Payment and Billing</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">Subscription Fees</h3>
                <ul className="list-disc list-inside text-neutral-700 space-y-1 ml-4">
                  <li>Subscription fees are billed in advance on a monthly or annual basis</li>
                  <li>All fees are non-refundable except as required by law</li>
                  <li>We reserve the right to change pricing with 30 days' notice</li>
                  <li>Failure to pay may result in suspension or termination of your account</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">Automatic Renewal</h3>
                <p className="text-neutral-700">
                  Subscriptions automatically renew unless cancelled before the renewal date. You
                  may cancel your subscription at any time through your account settings.
                </p>
              </div>
            </div>
          </section>

          {/* Service Availability */}
          <section>
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Service Availability</h2>
            <p className="text-neutral-700 leading-relaxed">
              We strive to maintain high availability but do not guarantee uninterrupted or
              error-free service. The Service may be unavailable due to:
            </p>
            <ul className="list-disc list-inside text-neutral-700 space-y-1 ml-4 mt-4">
              <li>Scheduled maintenance and updates</li>
              <li>Unforeseen technical issues or outages</li>
              <li>Third-party service dependencies</li>
              <li>Force majeure events</li>
            </ul>
            <p className="text-neutral-700 leading-relaxed mt-4">
              We will make reasonable efforts to notify users of planned maintenance and minimize
              service disruptions.
            </p>
          </section>

          {/* Termination */}
          <section>
            <div className="flex items-center mb-4">
              <XCircle className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-neutral-900">Termination</h2>
            </div>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">Termination by You</h3>
                <p className="text-neutral-700">
                  You may terminate your account at any time by contacting us or using account
                  settings. Upon termination, your access will be immediately suspended.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">Termination by Us</h3>
                <p className="text-neutral-700">
                  We may suspend or terminate your account if you:
                </p>
                <ul className="list-disc list-inside text-neutral-700 space-y-1 ml-4 mt-2">
                  <li>Violate these Terms or our policies</li>
                  <li>Engage in fraudulent or illegal activities</li>
                  <li>Fail to pay subscription fees</li>
                  <li>Pose a security risk to the Service or other users</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">Data After Termination</h3>
                <p className="text-neutral-700">
                  After termination, you may request export of your data within 30 days. We may
                  retain data as required by law or for legitimate business purposes.
                </p>
              </div>
            </div>
          </section>

          {/* Disclaimers */}
          <section>
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Disclaimers</h2>
            <p className="text-neutral-700 leading-relaxed mb-4">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
              EXPRESS OR IMPLIED. WE DISCLAIM ALL WARRANTIES, INCLUDING:
            </p>
            <ul className="list-disc list-inside text-neutral-700 space-y-1 ml-4">
              <li>Merchantability, fitness for a particular purpose, and non-infringement</li>
              <li>Accuracy, completeness, or reliability of forecasts and predictions</li>
              <li>Uninterrupted, secure, or error-free operation</li>
              <li>Compatibility with all systems or third-party integrations</li>
            </ul>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Limitation of Liability</h2>
            <p className="text-neutral-700 leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT,
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFITS, DATA
              LOSS, OR BUSINESS INTERRUPTION, ARISING FROM YOUR USE OF THE SERVICE.
            </p>
            <p className="text-neutral-700 leading-relaxed mt-4">
              Our total liability shall not exceed the amount you paid us in the 12 months
              preceding the claim.
            </p>
          </section>

          {/* Indemnification */}
          <section>
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Indemnification</h2>
            <p className="text-neutral-700 leading-relaxed">
              You agree to indemnify and hold harmless Finance BPA, its officers, directors, employees,
              and agents from any claims, damages, losses, liabilities, and expenses (including
              legal fees) arising from:
            </p>
            <ul className="list-disc list-inside text-neutral-700 space-y-1 ml-4 mt-4">
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
              <li>Inaccurate or fraudulent financial data you provide</li>
            </ul>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Governing Law</h2>
            <p className="text-neutral-700 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the
              jurisdiction in which Finance BPA operates, without regard to conflict of law
              principles. Any disputes shall be resolved through binding arbitration or in the
              appropriate courts.
            </p>
          </section>

          {/* Changes to Terms */}
          <section>
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Changes to Terms</h2>
            <p className="text-neutral-700 leading-relaxed">
              We reserve the right to modify these Terms at any time. We will notify you of
              material changes via email or through the Service. Your continued use after changes
              become effective constitutes acceptance of the modified Terms.
            </p>
          </section>

          {/* Contact Information */}
          <section className="bg-neutral-50 rounded-lg p-6">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Contact Us</h2>
            <p className="text-neutral-700 leading-relaxed mb-4">
              If you have questions about these Terms, please contact us:
            </p>
            <div className="space-y-2 text-neutral-700">
              <p>
                <strong>Email:</strong>{' '}
                <a href="mailto:legal@kreupai.com" className="text-primary-600 hover:underline">
                  legal@kreupai.com
                </a>
              </p>
              <p>
                <strong>Support:</strong>{' '}
                <a href="mailto:support@kreupai.com" className="text-primary-600 hover:underline">
                  support@kreupai.com
                </a>
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;

